import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { Loader2 } from 'lucide-react'
import type { AdminRole } from '@/types/database'

interface ProtectedRouteProps {
  requiredRole?: AdminRole
}

// Paths that editors cannot access
const EDITOR_RESTRICTED_PATHS = ['/admin/team', '/admin/settings']

export function ProtectedRoute({ requiredRole }: ProtectedRouteProps) {
  const { user, adminUser, isLoading, adminRole } = useAuth()
  const location = useLocation()

  // Show loading spinner while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // Not logged in - redirect to home with auth modal
  if (!user) {
    const redirectUrl = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`/?auth=signin&redirect=${redirectUrl}`} replace />
  }

  // Admin route requires admin user
  if (requiredRole === 'admin' || requiredRole === 'editor' || requiredRole === 'owner') {
    if (!adminUser) {
      // Logged in but not an admin - redirect to home
      return <Navigate to="/" replace />
    }

    // Check editor restrictions
    if (adminRole === 'editor') {
      const isRestricted = EDITOR_RESTRICTED_PATHS.some((path) =>
        location.pathname.startsWith(path)
      )
      if (isRestricted) {
        return <Navigate to="/admin" replace />
      }
    }
  }

  return <Outlet />
}
